// Which shot the race calls for right now. Pure: the director keeps the memory and timing.
// Priority (ux/index.md "Cameras"): catnip catch and landing first, then a close finish,
// a duel for the line, a lead change, ham, and otherwise varied race shots of the leading pack.
import type { CatId, RaceEvent, RaceSnapshot } from '../race/types';
import { duel } from './drama';
import { leader, type TripInfo } from './rigs';
import type { Shot, ShotKind } from './types';

export const TRIP_MS = 10_000;
/** Cut to the high landing shot this long into a trip, before the cat comes down. */
export const LANDING_CUT_S = 8.3;
/** How long the landing shot stays after the cat lands. */
export const AFTER_LANDING_MS = 3000;

export const PRIORITY = {
  signalLost: 100,
  finishReplay: 98,
  landing: 95,
  trip: 90,
  phase: 80,
  closeFinish: 75,
  finishLine: 70,
  duel: 68,
  straggler: 65,
  leadChange: 55,
  ham: 45,
  rotation: 10,
} as const;

/** What the director remembers between frames. */
export interface DirectorMemory {
  trips: TripInfo[];
  /** Landings that happened recently (render time ms) — the camera lingers on them. */
  landings: { cat: CatId; lane: number; to: number; t: number }[];
  /** Events seen in the last few seconds, with their render times. */
  recent: RaceEvent[];
  /** Cats whose ham snack the director already showed. */
  hamShown: Set<CatId>;
  /** Render time (ms) of the last lead-change and ham shots, so they do not repeat too often. */
  lastShown?: { leadChange: number; ham: number };
}

/** The start is a scramble of lead changes; leave it to the coverage for this long. */
export const LEAD_CHANGE_SETTLE_MS = 12_000;
export const LEAD_CHANGE_GAP_MS = 9000;
export const HAM_GAP_MS = 15_000;
/** The last-place special changes angle this often, so a long wait does not sit on one shot. */
export const STRAGGLER_BEAT_MS = 8000;
/** The finish camera stays on a photo finish this long after the crossing. */
export const CLOSE_FINISH_HOLD_MS = 2000;

const shot = (kind: ShotKind, subject: CatId | null, priority: number, reason: string): Shot => ({ kind, subject, priority, reason });

/** The event-driven shot, or null when nothing in particular is happening. */
export function eventShot(s: RaceSnapshot, peek: RaceEvent[], m: DirectorMemory): Shot | null {
  if (s.signal === 'lost') return shot('overview', null, PRIORITY.signalLost, 'signal lost');
  if (s.phase === 'starting') return shot('gates', null, PRIORITY.phase, 'at the gates');
  if (s.phase === 'finished') return shot('podium', s.results?.[0]?.id ?? null, PRIORITY.phase, 'results');

  // The catnip trip: the main event. The oldest trip wins if two overlap.
  const trip = [...m.trips].sort((a, b) => b.elapsed - a.elapsed)[0];
  if (trip) {
    const landingSoon = peek.some((e) => e.type === 'catnip-landing' && e.cat === trip.cat);
    if (trip.elapsed >= LANDING_CUT_S || landingSoon) return shot('landingHigh', trip.cat, PRIORITY.landing, 'catnip landing');
    if (trip.elapsed < 2) return shot('catch', trip.cat, PRIORITY.trip, 'catnip catch');
    return shot('tripFollow', trip.cat, PRIORITY.trip, 'catnip trip');
  }
  const landing = m.landings.find((l) => s.t - l.t < AFTER_LANDING_MS);
  if (landing) return shot('landingHigh', landing.cat, PRIORITY.landing, 'catnip landed');

  const running = s.cats.filter((c) => !c.finished);
  const lead = leader(s);
  // A close finish coming up, or the leader about to reach the line.
  const closeJustNow = m.recent.some((e) => e.type === 'close-finish' && s.t - e.t < CLOSE_FINISH_HOLD_MS);
  if (peek.some((e) => e.type === 'close-finish') || closeJustNow) return shot('finishLine', null, PRIORITY.closeFinish, 'close finish');
  const nearLine = running.some((c) => c.displayPos > 0.955 && !c.tripping);
  const justFinished = m.recent.some((e) => e.type === 'finish' && s.t - e.t < 2500);
  if (running.length > 1 && (nearLine || justFinished || peek.some((e) => e.type === 'finish'))) {
    const closest = running.filter((c) => !c.tripping).sort((a, b) => b.displayPos - a.displayPos)[0] ?? lead;
    return shot('finishLine', closest?.id ?? null, PRIORITY.finishLine, 'cats at the line');
  }
  // The last cat still out on the course, everyone else home.
  if (running.length === 1 && s.cats.length > 1) {
    const kinds: ShotKind[] = ['straggler', 'chase', 'straggler', 'overview'];
    const kind = kinds[Math.floor(s.t / STRAGGLER_BEAT_MS) % kinds.length]!;
    return shot(kind, running[0]!.id, PRIORITY.straggler, 'last place special');
  }

  // Two cats fighting for the line: side by side in one frame.
  const d = duel(s);
  if (d) return { ...shot('duel', d.leader.id, PRIORITY.duel, 'duel for the line'), rival: d.rival.id };

  const last = m.lastShown ?? { leadChange: -Infinity, ham: -Infinity };
  const change = [...m.recent].reverse().find((e) => e.type === 'lead-change' && s.t - e.t < 2000);
  if (change && change.type === 'lead-change' && s.phaseElapsedMs > LEAD_CHANGE_SETTLE_MS && s.t - last.leadChange > LEAD_CHANGE_GAP_MS) {
    return shot('frontQuarter', change.leader, PRIORITY.leadChange, 'new leader');
  }

  const eater = s.cats.find((c) => c.eating && !m.hamShown.has(c.id));
  if (eater && s.t - last.ham > HAM_GAP_MS) return shot('ham', eater.id, PRIORITY.ham, 'ham snack');
  return null;
}

/** Varied race coverage when nothing else is happening. Each entry: kind, which cat, seconds. */
const ROTATION: { kind: ShotKind; who: 'leader' | 'second' | 'third' | 'none'; seconds: number }[] = [
  { kind: 'pack', who: 'none', seconds: 7 },
  { kind: 'chase', who: 'leader', seconds: 6 },
  { kind: 'frontQuarter', who: 'leader', seconds: 5 },
  { kind: 'pack', who: 'none', seconds: 6 },
  { kind: 'chase', who: 'second', seconds: 5 },
  { kind: 'overview', who: 'none', seconds: 5 },
  { kind: 'frontQuarter', who: 'third', seconds: 5 },
];

export function rotationShot(s: RaceSnapshot, index: number): { shot: Shot; seconds: number } {
  const r = ROTATION[((index % ROTATION.length) + ROTATION.length) % ROTATION.length]!;
  const place = r.who === 'leader' ? 1 : r.who === 'second' ? 2 : r.who === 'third' ? 3 : 0;
  const live = s.cats.filter((c) => !c.tripping && !c.finished);
  const ranked = [...live].sort((a, b) => a.place - b.place);
  const cat = place ? ranked[place - 1] ?? ranked[0] ?? leader(s) : null;
  return { shot: shot(r.kind, cat?.id ?? null, PRIORITY.rotation, 'race coverage'), seconds: r.seconds };
}
